/**
 * Printable pack of internal warehouse / production documents for one
 * produced lot. Layout follows the Ritam primka (Boogie Bakery letterhead),
 * without purchase prices / VAT.
 */

export type DocLine = {
  article_code?: string | null;
  name?: string | null;
  unit?: string | null;
  quantity?: number | null;
  lot?: string | null;
  material_lot_id?: number | null;
  expiry?: string | null;
};

export type DocImage = { url: string; label?: string | null; receipt_number?: string | null };

export type DocReceipt = {
  id: number;
  number?: string | null;
  date?: string | null;
  warehouse?: string | null;
  warehouse_code?: string | null;
  supplier_name?: string | null;
  supplier_code?: string | null;
  supplier_oib?: string | null;
  supplier_doc?: string | null;
  supplier_doc_date?: string | null;
  notes?: string | null;
  lines: DocLine[];
  images?: DocImage[];
};

export type DocTransfer = {
  id: number;
  number?: string | null;
  date?: string | null;
  purpose?: "inbound" | "finished_goods" | "writeoff" | "other" | string | null;
  from_warehouse?: string | null;
  to_warehouse?: string | null;
  from_code?: string | null;
  to_code?: string | null;
  notes?: string | null;
  created_by?: string | null;
  lines: DocLine[];
};

export type DocComponent = {
  name?: string | null;
  lot?: string | null;
  lot_id?: number | null;
  receipt_id?: number | null;
  receipt_number?: string | null;
  transfer_ids?: number[];
};

export type DocWorkOrder = {
  batch_id: number;
  product_name?: string | null;
  production_lot?: string | null;
  mix_code?: string | null;
  production_date?: string | null;
  final_mass_kg?: number | null;
  packed_pieces?: number | null;
  produced_pieces?: number | null;
  recipe_code?: string | null;
  is_semi_finished?: boolean;
  phases?: {
    name?: string | null;
    start?: string | null;
    end?: string | null;
    duration_min?: number | null;
    temp_c?: number | null;
    machine_name?: string | null;
  }[];
  inputs?: {
    name?: string | null;
    lot?: string | null;
    expiry?: string | null;
    quantity?: number | null;
    unit?: string | null;
    receipt?: string | null;
  }[];
};

export type DocumentationPack = {
  issued_at?: string;
  batch: {
    batch_id: number;
    production_lot_code?: string | null;
    product_name?: string | null;
    production_date?: string | null;
    mix_code?: string | null;
  };
  components?: DocComponent[];
  work_orders: DocWorkOrder[];
  receipts: DocReceipt[];
  transfers: DocTransfer[];
  scans: DocImage[];
};

const COMPANY = {
  name: "Boogie Bakery d.o.o.",
  address: "Put Vranskog jezera 3",
  city: "23211 Pakoštane",
  oib: "89068676216",
  pdv: "HR89068676216",
  swift: "ESBCHR22",
  tel: "0996038586",
  email: "office@boogiebakery.com",
  iban: "HR0424020061100977116",
};

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("hr-HR") : "—");
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtQty = (v?: number | null, unit?: string | null) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const u = (unit || "").toUpperCase();
  if (u === "KOM" || u === "PCS" || u === "KOM.") {
    return Number.isInteger(n) ? n.toLocaleString("hr-HR") : n.toLocaleString("hr-HR", { maximumFractionDigits: 3 });
  }
  return n.toLocaleString("hr-HR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};
const dash = (v?: string | null) => (v && String(v).trim() ? String(v).trim() : "—");
const nowStamp = () =>
  new Date().toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "medium" }).replace(",", "");

const CSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; background: #ececec; }
  .no-print { margin: 12px auto; width: 210mm; text-align: right; }
  .no-print button { font-size: 13px; padding: 8px 16px; cursor: pointer; }
  .page {
    background: #fff; width: 210mm; min-height: 297mm; margin: 10px auto;
    padding: 12mm 12mm 16mm; box-shadow: 0 1px 6px rgba(0,0,0,0.18);
    page-break-after: always; display: flex; flex-direction: column;
  }
  .page:last-child { page-break-after: auto; }
  .co { width: 100%; border-collapse: collapse; margin: 0 0 8px; }
  .co td { border: none; padding: 0; vertical-align: top; font-size: 10px; line-height: 1.35; }
  .co .left { width: 55%; }
  .co .right { width: 45%; text-align: right; }
  .co .name { font-weight: bold; font-size: 12px; }
  h1.doc-title { font-size: 22px; font-weight: bold; margin: 6px 0 10px; letter-spacing: 0.2px; }
  .meta-wrap { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 12px; }
  .meta { flex: 1; }
  .meta table { width: 100%; border-collapse: collapse; }
  .meta td { border: none; padding: 1px 0; vertical-align: top; font-size: 11px; }
  .meta td.k { width: 34%; font-weight: bold; padding-right: 8px; white-space: nowrap; }
  .box {
    flex: 0 0 48%; border: 1px solid #888; border-radius: 18px; padding: 8px 12px 10px;
    font-size: 10.5px; line-height: 1.45;
  }
  .box .box-h { font-weight: bold; margin-bottom: 4px; }
  .box table { width: 100%; border-collapse: collapse; }
  .box td { border: none; padding: 1px 0; }
  .box td.k { width: 42%; }
  table.lines { width: 100%; border-collapse: collapse; margin: 8px 0 4px; font-size: 10px; }
  table.lines th {
    background: #5a5a5a; color: #fff; border: 1px solid #444; padding: 4px 6px;
    text-align: left; font-weight: bold; font-size: 9.5px;
  }
  table.lines td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
  table.lines td.c, table.lines th.c { text-align: center; }
  table.lines td.r, table.lines th.r { text-align: right; }
  table.lines tr.tot td { font-weight: bold; background: #f3f3f3; }
  .signs { display: flex; justify-content: space-between; margin: 36px 24px 0; }
  .signs div { width: 38%; text-align: center; }
  .signs .lab { margin-bottom: 28px; font-size: 11px; }
  .signs .line { border-top: 1px solid #111; padding-top: 4px; font-size: 10px; color: #444; }
  .foot {
    margin-top: auto; padding-top: 10px; font-size: 9px; color: #555;
    display: flex; justify-content: space-between; border: none;
  }
  .toc { width: 100%; border-collapse: collapse; margin-top: 8px; }
  .toc th, .toc td { border: 1px solid #bbb; padding: 5px 8px; text-align: left; }
  .toc th { background: #f0f0f0; }
  .scan-frame { flex: 1; display: flex; align-items: center; justify-content: center; min-height: 200mm; }
  .scan-frame img { max-width: 100%; max-height: 230mm; object-fit: contain; }
  .note { font-size: 10px; color: #555; font-style: italic; margin: 4px 0 8px; }
  .group-head {
    background: #222; color: #fff; padding: 6px 10px; margin: 0 0 10px;
    font-size: 11px; font-weight: bold; letter-spacing: 0.03em;
  }
  .group-head span { font-weight: normal; opacity: 0.85; margin-left: 8px; }
  @media print {
    body { background: #fff; }
    .page { width: auto; min-height: auto; margin: 0; box-shadow: none; padding: 0; }
    .no-print { display: none; }
    table.lines tr { page-break-inside: avoid; }
  }
  @page { size: A4; margin: 12mm 12mm 14mm; }
`;

const letterhead = () => `
  <table class="co"><tr>
    <td class="left">
      <div class="name">${esc(COMPANY.name)}</div>
      ${esc(COMPANY.address)}<br/>
      ${esc(COMPANY.city)}<br/>
      OIB: ${esc(COMPANY.oib)}
    </td>
    <td class="right">
      PDV ID broj: ${esc(COMPANY.pdv)}<br/>
      SWIFT: ${esc(COMPANY.swift)} &nbsp; tel: ${esc(COMPANY.tel)}<br/>
      e-mail: ${esc(COMPANY.email)}<br/>
      IBAN: ${esc(COMPANY.iban)}
    </td>
  </tr></table>
`;

const footer = (kind: string, number: string, page = "1/1") => `
  <div class="foot">
    <span>${esc(nowStamp())}</span>
    <span>${esc(kind)}${number ? ` br.${esc(number)}` : ""}</span>
    <span>Oblik: Standardni</span>
    <span>${esc(page)}</span>
  </div>
`;

const signs = () => `
  <div class="signs">
    <div><div class="lab">Kalkulirao:</div><div class="line">&nbsp;</div></div>
    <div><div class="lab">Odobrio:</div><div class="line">&nbsp;</div></div>
  </div>
`;

const linesTable = (rows: DocLine[], withLot = true) => {
  const head = withLot
    ? `<tr><th class="c">Br.</th><th>Šifra artikla</th><th>Naziv artikla</th><th>Lot</th><th class="c">Rok</th><th class="c">Mjera</th><th class="r">Količina</th></tr>`
    : `<tr><th class="c">Br.</th><th>Šifra artikla</th><th>Naziv artikla</th><th class="c">Mjera</th><th class="r">Količina</th></tr>`;
  const body = (rows.length ? rows : [{ name: "—" }]).map((l, i) => {
    const qty = fmtQty(l.quantity, l.unit);
    if (withLot) {
      return `<tr><td class="c">${i + 1}</td><td>${esc(dash(l.article_code))}</td><td>${esc(dash(l.name))}</td><td>${esc(dash(l.lot))}</td><td class="c">${esc(fmtDate(l.expiry))}</td><td class="c">${esc(dash(l.unit))}</td><td class="r">${esc(qty)}</td></tr>`;
    }
    return `<tr><td class="c">${i + 1}</td><td>${esc(dash(l.article_code))}</td><td>${esc(dash(l.name))}</td><td class="c">${esc(dash(l.unit))}</td><td class="r">${esc(qty)}</td></tr>`;
  }).join("");
  const tot = rows.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const totUnit = rows.every((l) => (l.unit || "").toUpperCase() === (rows[0]?.unit || "").toUpperCase())
    ? rows[0]?.unit
    : null;
  const totCells = withLot
    ? `<td colspan="5"></td><td class="c">${esc(totUnit || "")}</td><td class="r">${esc(fmtQty(tot, totUnit))}</td>`
    : `<td colspan="3"></td><td class="c">${esc(totUnit || "")}</td><td class="r">${esc(fmtQty(tot, totUnit))}</td>`;
  return `<table class="lines"><thead>${head}</thead><tbody>${body}<tr class="tot">${totCells}</tr></tbody></table>`;
};

const wrapPage = (inner: string) => `<div class="page">${inner}</div>`;

const componentBanner = (c?: DocComponent | null, kind?: string | null) => {
  if (!c?.name && !kind) return "";
  const title = c?.name || kind || "";
  const sub = [c?.lot ? `lot ${c.lot}` : null, kind && c?.name ? kind : null].filter(Boolean).join(" · ");
  return `<div class="group-head">${esc(title)}${sub ? `<span>${esc(sub)}</span>` : ""}</div>`;
};

const isOutboundTransfer = (t: DocTransfer) => t.purpose !== "inbound";

const linesForComponent = (lines: DocLine[], c: DocComponent): DocLine[] => {
  if (c.lot_id != null) {
    const byId = lines.filter((l) => l.material_lot_id === c.lot_id);
    if (byId.length) return byId;
  }
  const lot = (c.lot || "").trim().toLowerCase();
  if (lot) {
    const byLot = lines.filter((l) => (l.lot || "").trim().toLowerCase() === lot);
    if (byLot.length) return byLot;
  }
  const name = (c.name || "").trim().toLowerCase();
  if (name) {
    const byName = lines.filter((l) => {
      const n = (l.name || "").toLowerCase();
      return n && (n === name || n.includes(name) || name.includes(n));
    });
    if (byName.length) return byName;
  }
  return [];
};

const scansForReceipt = (scans: DocImage[], receiptNumber?: string | null, c?: DocComponent | null) => {
  if (!receiptNumber) return [];
  const wantLot = (c?.lot || "").trim().toLowerCase();
  const matched = scans.filter((s) => {
    if (s.receipt_number !== receiptNumber) return false;
    const label = (s.label || "").trim();
    const lotFromLabel = /^Lot\s+/i.test(label) ? label.replace(/^Lot\s+/i, "").trim().toLowerCase() : "";
    if (lotFromLabel && wantLot && lotFromLabel !== wantLot) return false;
    return true;
  });
  return [...matched].sort((a, b) => {
    const rank = (s: DocImage) => (/lot/i.test(s.label || "") ? 1 : 0);
    return rank(a) - rank(b);
  });
};

const packComponents = (data: DocumentationPack): DocComponent[] => {
  if (data.components?.length) return data.components;
  return (data.receipts || []).map((r) => ({
    name: r.lines[0]?.name || `Primka ${r.number || r.id}`,
    lot: r.lines[0]?.lot ?? null,
    lot_id: r.lines[0]?.material_lot_id ?? null,
    receipt_id: r.id,
    receipt_number: r.number ?? null,
    transfer_ids: [],
  }));
};

const coverPage = (data: DocumentationPack) => {
  const lot = data.batch.production_lot_code || data.batch.mix_code || `#${data.batch.batch_id}`;
  const components = packComponents(data);
  const inbound = (data.transfers || []).filter((t) => !isOutboundTransfer(t));
  const outbound = (data.transfers || []).filter(isOutboundTransfer);
  const toc: string[] = [];
  for (const c of components) {
    toc.push(
      `<tr><td colspan="2"><strong>${esc(c.name || "Sastavnica")}</strong>${c.lot ? ` · ${esc(c.lot)}` : ""}</td></tr>`
    );
    for (const s of scansForReceipt(data.scans || [], c.receipt_number)) {
      toc.push(`<tr><td>Otpremnica (sken)</td><td>${esc([s.label, s.receipt_number].filter(Boolean).join(" · "))}</td></tr>`);
    }
    if (c.receipt_number || c.receipt_id) {
      const r = (data.receipts || []).find((x) => x.id === c.receipt_id || x.number === c.receipt_number);
      toc.push(
        `<tr><td>Primka ${esc(c.receipt_number || "")}</td><td>${esc(r?.supplier_name || "")}</td></tr>`
      );
    }
    for (const tid of inboundIdsForComponent(c, inbound)) {
      const t = inbound.find((x) => x.id === tid);
      if (t) {
        toc.push(
          `<tr><td>Međuskladišnica ${esc(t.number || "")}</td><td>${esc([t.from_code || t.from_warehouse, t.to_code || t.to_warehouse].filter(Boolean).join(" → "))}</td></tr>`
        );
      }
    }
  }
  toc.push(`<tr><td colspan="2"><strong>Radni nalozi</strong></td></tr>`);
  for (const w of data.work_orders || []) {
    toc.push(
      `<tr><td>Radni nalog${w.is_semi_finished ? " · poluproizvod" : ""}</td><td>${esc([w.product_name, w.production_lot || w.mix_code].filter(Boolean).join(" · "))}</td></tr>`
    );
  }
  toc.push(`<tr><td colspan="2"><strong>Izlazne međuskladišnice</strong></td></tr>`);
  for (const t of outbound) {
    const kind = t.purpose === "writeoff" ? "otpis" : t.purpose === "finished_goods" ? "gotovi proizvodi" : "izlaz";
    toc.push(
      `<tr><td>Međuskladišnica ${esc(t.number || "")}</td><td>${esc(kind)} · ${esc([t.from_code || t.from_warehouse, t.to_code || t.to_warehouse].filter(Boolean).join(" → "))}</td></tr>`
    );
  }
  return wrapPage(`
    ${letterhead()}
    <h1 class="doc-title">Dokumentacija sljedivosti</h1>
    <div class="meta">
      <table>
        <tr><td class="k">Proizvod:</td><td><strong>${esc(dash(data.batch.product_name))}</strong></td></tr>
        <tr><td class="k">Lot:</td><td><strong>${esc(lot)}</strong></td></tr>
        <tr><td class="k">Datum proizvodnje:</td><td>${esc(fmtDate(data.batch.production_date))}</td></tr>
        <tr><td class="k">Izrađeno:</td><td>${esc(fmtDateTime(data.issued_at))}</td></tr>
      </table>
    </div>
    <p class="note">Dokumenti su grupirani po sastavnici (otpremnica → primka → ulazna međuskladišnica). Radni nalozi i izlazne međuskladišnice su na kraju. Cijene i PDV nisu dio ovog izvoda.</p>
    <table class="toc">
      <thead><tr><th>Vrsta</th><th>Dokument</th></tr></thead>
      <tbody>${toc.join("")}</tbody>
    </table>
    ${footer("Dokumentacija sljedivosti", String(lot))}
  `);
};

const workOrderPage = (w: DocWorkOrder) => {
  const num = w.production_lot || w.mix_code || String(w.batch_id);
  const qty = [
    w.produced_pieces != null ? `${w.produced_pieces} kom proizvedeno` : null,
    w.packed_pieces != null ? `${w.packed_pieces} kom spakirano` : null,
    w.final_mass_kg != null ? `${fmtQty(w.final_mass_kg, "KG")} kg` : null,
  ].filter(Boolean).join(" · ") || "—";
  const phaseRows = (w.phases || []).map((p, i) =>
    `<tr><td class="c">${i + 1}</td><td>${esc(dash(p.name))}</td><td class="c">${esc(fmtDateTime(p.start))}</td><td class="c">${esc(fmtDateTime(p.end))}</td><td class="r">${p.duration_min != null ? `${p.duration_min} min` : "—"}</td><td>${esc(dash(p.machine_name))}</td></tr>`
  ).join("");
  const inpRows = (w.inputs || []).map((l, i) =>
    `<tr><td class="c">${i + 1}</td><td>${esc(dash(l.name))}</td><td>${esc(dash(l.lot))}</td><td class="c">${esc(fmtDate(l.expiry))}</td><td class="c">${esc(dash(l.unit))}</td><td class="r">${esc(fmtQty(l.quantity, l.unit))}</td><td>${esc(dash(l.receipt))}</td></tr>`
  ).join("");
  return wrapPage(`
    ${componentBanner({ name: w.product_name || "Radni nalog" }, w.is_semi_finished ? "Radni nalog · poluproizvod" : "Radni nalog")}
    ${letterhead()}
    <h1 class="doc-title">Radni nalog${w.is_semi_finished ? " · poluproizvod" : ""}</h1>
    <div class="meta">
      <table>
        <tr><td class="k">Proizvod:</td><td><strong>${esc(dash(w.product_name))}</strong></td></tr>
        <tr><td class="k">Lot / šifra:</td><td><strong>${esc(num)}</strong>${w.mix_code && w.mix_code !== num ? ` · ${esc(w.mix_code)}` : ""}</td></tr>
        <tr><td class="k">Datum:</td><td>${esc(fmtDate(w.production_date))}</td></tr>
        <tr><td class="k">Receptura:</td><td>${esc(dash(w.recipe_code))}</td></tr>
        <tr><td class="k">Količina:</td><td>${esc(qty)}</td></tr>
      </table>
    </div>
    <table class="lines">
      <thead><tr><th class="c">Br.</th><th>Faza</th><th class="c">Početak</th><th class="c">Kraj</th><th class="r">Trajanje</th><th>Stroj</th></tr></thead>
      <tbody>${phaseRows || `<tr><td colspan="6">Nema evidentiranih faza</td></tr>`}</tbody>
    </table>
    <table class="lines">
      <thead><tr><th class="c">Br.</th><th>Sirovina</th><th>Lot</th><th class="c">Rok</th><th class="c">Mjera</th><th class="r">Količina</th><th>Primka</th></tr></thead>
      <tbody>${inpRows || `<tr><td colspan="7">Nema utrošenih sirovina</td></tr>`}</tbody>
    </table>
    ${signs()}
    ${footer("Radni nalog", String(num))}
  `);
};

const receiptPage = (r: DocReceipt, group?: DocComponent | null) => {
  const num = r.number || String(r.id);
  const supplier = [r.supplier_code, r.supplier_name].filter(Boolean).join("/");
  return wrapPage(`
    ${componentBanner(group, "Primka")}
    ${letterhead()}
    <h1 class="doc-title">Primka br.${esc(num)}</h1>
    <div class="meta-wrap">
      <div class="meta">
        <table>
          <tr><td class="k">Radna jedinica:</td><td><strong>${esc(dash(r.warehouse))}</strong></td></tr>
          <tr><td class="k">Datum:</td><td>${esc(fmtDate(r.date))}</td></tr>
          <tr><td class="k">Dobavljač:</td><td>${esc(dash(supplier))}</td></tr>
          <tr><td class="k">OIB dobavljača:</td><td>${esc(dash(r.supplier_oib))}</td></tr>
          <tr><td class="k">Dokument dobavljača:</td><td>${esc(dash(r.supplier_doc))}</td></tr>
          <tr><td class="k">Napomena:</td><td>${esc(dash(r.notes))}</td></tr>
        </table>
      </div>
      <div class="box">
        <div class="box-h">Dokument dobavljača</div>
        <table>
          <tr><td class="k">Datum:</td><td>${esc(fmtDate(r.supplier_doc_date || r.date))}</td></tr>
          <tr><td class="k">Isporuka:</td><td>${esc(fmtDate(r.date))}</td></tr>
          <tr><td class="k">Broj:</td><td>${esc(dash(r.supplier_doc))}</td></tr>
          <tr><td class="k">Tip računa:</td><td>Otpremnica</td></tr>
        </table>
      </div>
    </div>
    ${linesTable(r.lines, true)}
    ${signs()}
    ${footer("Primka", String(num))}
  `);
};

const outboundKindLabel = (t: DocTransfer) => {
  if (t.purpose === "writeoff") return "Izlaz · otpis";
  if (t.purpose === "finished_goods") return "Izlaz · gotovi proizvodi";
  if (t.purpose === "inbound") return "Ulazna međuskladišnica";
  return "Međuskladišnica";
};

const transferPage = (t: DocTransfer, group?: DocComponent | null, kind?: string | null) => {
  const num = t.number || String(t.id);
  return wrapPage(`
    ${componentBanner(group, kind || outboundKindLabel(t))}
    ${letterhead()}
    <h1 class="doc-title">Međuskladišnica br.${esc(num)}</h1>
    <div class="meta-wrap">
      <div class="meta">
        <table>
          <tr><td class="k">Datum:</td><td>${esc(fmtDate(t.date))}</td></tr>
          <tr><td class="k">Iz skladišta:</td><td><strong>${esc(dash([t.from_code, t.from_warehouse].filter(Boolean).join(" · ")))}</strong></td></tr>
          <tr><td class="k">U skladište:</td><td><strong>${esc(dash([t.to_code, t.to_warehouse].filter(Boolean).join(" · ")))}</strong></td></tr>
          <tr><td class="k">Izradio:</td><td>${esc(dash(t.created_by))}</td></tr>
          <tr><td class="k">Napomena:</td><td>${esc(dash(t.notes))}</td></tr>
        </table>
      </div>
    </div>
    ${linesTable(t.lines, true)}
    ${signs()}
    ${footer("Međuskladišnica", String(num))}
  `);
};

const scanPage = (s: DocImage, i: number, total: number, group?: DocComponent | null) => wrapPage(`
  ${componentBanner(group, s.label || "Otpremnica")}
  ${letterhead()}
  <h1 class="doc-title">${esc(s.label || "Otpremnica (sken)")}</h1>
  <div class="meta">
    <table>
      <tr><td class="k">Primka:</td><td>${esc(dash(s.receipt_number))}</td></tr>
    </table>
  </div>
  <div class="scan-frame"><img src="${esc(s.url)}" alt="${esc(s.label || "Otpremnica")}" /></div>
  ${footer("Otpremnica (sken)", s.receipt_number || "", `${i + 1}/${total}`)}
`);

const inboundIdsForComponent = (c: DocComponent, inbound: DocTransfer[]) => {
  const ids = [...(c.transfer_ids || [])];
  if (!ids.length) {
    for (const t of inbound) {
      if (linesForComponent(t.lines, c).length) ids.push(t.id);
    }
  }
  return Array.from(new Set(ids));
};

const pagesForComponent = (
  c: DocComponent,
  data: DocumentationPack,
  inbound: DocTransfer[]
): { pages: string[]; receiptId?: number; transferIds: number[]; scanUrls: string[] } => {
  const pages: string[] = [];
  const scans = scansForReceipt(data.scans || [], c.receipt_number, c);
  scans.forEach((s, i) => pages.push(scanPage(s, i, scans.length, c)));
  const receipt = (data.receipts || []).find(
    (r) => r.id === c.receipt_id || (!!c.receipt_number && r.number === c.receipt_number)
  );
  if (receipt) {
    pages.push(receiptPage(receipt, c));
  }
  const transferIds = inboundIdsForComponent(c, inbound);
  for (const tid of transferIds) {
    const t = inbound.find((x) => x.id === tid);
    if (!t) continue;
    const filtered = linesForComponent(t.lines, c);
    pages.push(transferPage(filtered.length ? { ...t, lines: filtered } : t, c, "Ulazna međuskladišnica"));
  }
  return {
    pages,
    receiptId: receipt?.id,
    transferIds,
    scanUrls: scans.map((s) => s.url).filter(Boolean) as string[],
  };
};

export function buildDocumentationPackHtml(data: DocumentationPack): string {
  const lot = data.batch.production_lot_code || data.batch.mix_code || `#${data.batch.batch_id}`;
  const components = packComponents(data);
  const inbound = (data.transfers || []).filter((t) => !isOutboundTransfer(t));
  const outbound = [...(data.transfers || []).filter(isOutboundTransfer)].sort((a, b) => {
    const rank = (t: DocTransfer) => (t.purpose === "finished_goods" ? 0 : t.purpose === "writeoff" ? 1 : 2);
    return rank(a) - rank(b);
  });
  const usedReceipts = new Set<number>();
  const usedTransfers = new Set<number>();
  const usedScanUrls = new Set<string>();
  const componentPages: string[] = [];
  for (const c of components) {
    const built = pagesForComponent(c, data, inbound);
    if (built.receiptId) usedReceipts.add(built.receiptId);
    for (const tid of built.transferIds) usedTransfers.add(tid);
    for (const url of built.scanUrls) usedScanUrls.add(url);
    componentPages.push(...built.pages);
  }
  const leftoverGroup: DocComponent = { name: "Ostalo" };
  const leftoverPages: string[] = [];
  const leftoverScans = (data.scans || []).filter((s) => s.url && !usedScanUrls.has(s.url));
  leftoverScans.forEach((s, i) => leftoverPages.push(scanPage(s, i, leftoverScans.length, leftoverGroup)));
  for (const r of data.receipts || []) {
    if (!usedReceipts.has(r.id)) leftoverPages.push(receiptPage(r, leftoverGroup));
  }
  for (const t of inbound) {
    if (!usedTransfers.has(t.id)) leftoverPages.push(transferPage(t, leftoverGroup, "Ulazna međuskladišnica"));
  }
  const workOrders = data.work_orders || [];
  const pages = [
    coverPage(data),
    ...componentPages,
    ...leftoverPages,
    ...workOrders.map(workOrderPage),
    ...outbound.map((t) => transferPage(t, { name: outboundKindLabel(t) }, outboundKindLabel(t))),
  ].join("");

  return `<!DOCTYPE html>
<html lang="hr">
<head>
  <meta charset="utf-8" />
  <title>Dokumentacija · ${esc(lot)}</title>
  <style>${CSS}</style>
</head>
<body>
  <div class="no-print"><button type="button" onclick="window.print()">Ispis / spremi PDF</button></div>
  ${pages}
</body>
</html>`;
}

export function writeDocumentationPack(win: Window, data: DocumentationPack) {
  win.document.open();
  win.document.write(buildDocumentationPackHtml(data));
  win.document.close();
  win.opener = null;
  win.focus();
}
